import React, { useState } from 'react'
import {
  Container,
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  InputAdornment,
  alpha,
  useTheme,
  Stack,
  LinearProgress,
} from '@mui/material'
import { motion } from 'framer-motion'
import { Mail, ArrowLeft, Lock, Send, CheckCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { cookies } from '../../utils/cookie'
import Swal from 'sweetalert2'
import { useFormik } from 'formik'
import { forgotPasswordFormSchema } from '../../utils/formSchema/formSchemas'

const ModernForgotPassword = () => {
  const navigate = useNavigate()
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const submit = async (values) => {
    setIsLoading(true)
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/forgot-password`,
        {
          email: values.email,
        },
        {
          headers: {
            Authorization: `Bearer ${cookies.get('jwt-access')}`,
            'Content-Type': 'application/json',
          },
        }
      )

      setIsSubmitted(true)
      setIsLoading(false)

      Swal.fire({
        title: 'E-posta Gönderildi!',
        html: `
          <p>Şifre sıfırlama bağlantısı e-posta adresinize gönderildi.</p>
          <p style="margin-top: 12px; font-size: 0.9em; color: #666;">
            Lütfen gelen kutunuzu kontrol edin ve bağlantıya tıklayarak 
            şifrenizi sıfırlayın.
          </p>
        `,
        icon: 'success',
        confirmButtonText: 'Giriş Sayfasına Git',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      }).then(() => {
        navigate('/login')
      })
    } catch (err) {
      setIsLoading(false)
      Swal.fire({
        title: 'E-posta Gönderilemedi',
        text: err.response?.data?.message || 'Lütfen e-posta adresinizi kontrol edin',
        icon: 'error',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })
    }
  }

  const { values, errors, handleChange, handleSubmit } = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: forgotPasswordFormSchema,
    onSubmit: submit,
  })

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: theme.palette.background.default,
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {/* Gradient Orbs */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 2,
            }}
            sx={{
              position: 'absolute',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                theme.palette.primary.main,
                0.15
              )}, transparent)`,
              filter: 'blur(60px)',
              top: `${20 + i * 30}%`,
              left: `${10 + i * 30}%`,
            }}
          />
        ))}

        {/* Grid Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            opacity: 0.3,
          }}
        />

        {/* Floating Lock Icons */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              y: [0, -20, 0],
              opacity: [0.1, 0.3, 0.1],
              rotate: [0, 360],
            }}
            transition={{
              duration: 15 + i * 3,
              repeat: Infinity,
              ease: 'linear',
              delay: i * 2,
            }}
            sx={{
              position: 'absolute',
              top: `${10 + i * 20}%`,
              left: `${5 + i * 20}%`,
              opacity: 0.1,
            }}
          >
            <Lock size={40} color={theme.palette.primary.main} />
          </Box>
        ))}
      </Box>

      {/* Content */}
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.1)}`,
            textAlign: 'center',
          }}
        >
          {!isSubmitted ? (
            <>
              {/* Icon */}
              <Box
                component={motion.div}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                sx={{
                  display: 'inline-flex',
                  p: 3,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}, ${alpha(theme.palette.primary.main, 0.1)})`,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  mb: 3,
                }}
              >
                <Lock size={48} color={theme.palette.primary.main} />
              </Box>

              {/* Header */}
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                Şifrenizi mi Unuttunuz?
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: alpha('#fff', 0.7),
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                Endişelenmeyin! E-posta adresinizi girin, size şifre sıfırlama
                bağlantısı gönderelim.
              </Typography>

              {/* Email Field */}
              <Box sx={{ mb: 3, textAlign: 'left' }}>
                <TextField
                  id="email"
                  fullWidth
                  label="E-posta Adresi"
                  placeholder="ornek@email.com"
                  value={values.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Mail size={20} color={theme.palette.primary.main} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: alpha(theme.palette.background.default, 0.5),
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(theme.palette.primary.main, 0.4),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              {/* Submit Button */}
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                startIcon={<Send size={20} />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#000',
                  mb: 2,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
                  },
                  '&:disabled': {
                    background: alpha(theme.palette.primary.main, 0.5),
                    color: alpha('#000', 0.5),
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {isLoading ? 'Gönderiliyor...' : 'Sıfırlama Bağlantısı Gönder'}
              </Button>

              {isLoading && <LinearProgress sx={{ borderRadius: 1, mb: 3 }} />}

              {/* Back to Login */}
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/login')}
                startIcon={<ArrowLeft size={18} />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: theme.palette.primary.main,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                Giriş Sayfasına Dön
              </Button>
            </>
          ) : (
            // Success State
            <Box
              component={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              {/* Success Icon */}
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 3,
                  borderRadius: '50%',
                  background: alpha(theme.palette.success.main, 0.1),
                  border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  mb: 3,
                }}
              >
                <CheckCircle size={48} color={theme.palette.success.main} />
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 900,
                  color: theme.palette.success.main,
                  mb: 2,
                }}
              >
                E-posta Gönderildi!
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: alpha('#fff', 0.7),
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                Şifre sıfırlama talimatları <strong>{values.email}</strong>{' '}
                adresine gönderildi. Lütfen e-postanızı kontrol edin.
              </Typography>

              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/login')}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    color: '#000',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    },
                  }}
                >
                  Giriş Sayfasına Git
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setIsSubmitted(false)
                    setIsLoading(false)
                  }}
                  sx={{
                    color: alpha('#fff', 0.6),
                    textTransform: 'none',
                    '&:hover': {
                      color: theme.palette.primary.main,
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  E-posta almadınız mı? Tekrar gönder
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Help Text */}
        <Box
          sx={{
            mt: 3,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: alpha('#fff', 0.5),
              display: 'block',
              mb: 1,
            }}
          >
            E-posta almadınız mı?
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            <Typography
              variant="caption"
              sx={{
                color: alpha('#fff', 0.6),
              }}
            >
              • Spam klasörünü kontrol edin
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha('#fff', 0.6),
              }}
            >
              • E-posta adresinizi doğrulayın
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: alpha('#fff', 0.6),
              }}
            >
              • Birkaç dakika bekleyin
            </Typography>
          </Stack>
        </Box>
      </Container>
    </Box>
  )
}

export default ModernForgotPassword